import { apiClient } from './client';

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'archived';
export type TaskPriority = 'urgent' | 'high' | 'medium' | 'low';
export type TaskSource = 'manual' | 'fathom' | 'slack' | 'email' | 'api';

/** The four statuses rendered as board columns, in display order. */
export const BOARD_STATUSES: readonly TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done'];

export const STATUS_LABELS: Record<TaskStatus, string> = {
  todo: 'To do',
  in_progress: 'In progress',
  blocked: 'Blocked',
  done: 'Done',
  archived: 'Archived',
};

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

export const PRIORITIES: readonly TaskPriority[] = ['urgent', 'high', 'medium', 'low'];

/** Provenance for a task lifted out of a call. Absent on manual tasks. */
export interface TaskSourceMeta {
  recordingId?: number;
  meetingTitle?: string;
  shareUrl?: string | null;
  recordedAt?: string | null;
  timestamp?: string;
  quote?: string;
  speaker?: string;
}

export interface Task {
  id: string;
  title: string;
  notes: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  /** Date-only 'YYYY-MM-DD'. Never a timestamp — see the API's dueDate schema. */
  dueDate: string | null;
  remindAt: string | null;
  labels: string[];
  source: TaskSource;
  sourceRef: string | null;
  sourceMeta: TaskSourceMeta | null;
  suggestionId: string | null;
  /** LexoRank ordering key within the column. Opaque to the UI beyond sorting. */
  rank: string;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SuggestionEvidence {
  quote: string;
  speaker: string;
  timestamp: string;
  meetingTitle: string;
  shareUrl: string | null;
  recordedAt: string | null;
}

export interface TaskSuggestion {
  id: string;
  source: TaskSource;
  sourceRef: string;
  proposedTitle: string;
  proposedNotes: string | null;
  proposedDueDate: string | null;
  proposedPriority: TaskPriority | null;
  /** 0..1 as a string — Postgres numeric comes over the wire as text. */
  confidence: string | null;
  evidence: SuggestionEvidence | null;
  origin: 'llm' | 'fathom_action_item';
  state: 'pending' | 'accepted' | 'rejected' | 'superseded';
  createdAt: string;
}

export interface TaskCounts {
  todo: number;
  inProgress: number;
  blocked: number;
  done: number;
  open: number;
  overdue: number;
  dueToday: number;
  pendingSuggestions: number;
}

export interface CreateTaskInput {
  title: string;
  notes?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  dueDate?: string | null;
  /** ISO instant for an exact-time Slack reminder. */
  remindAt?: string | null;
  labels?: string[];
  insertAt?: 'top' | 'bottom';
}

export type TaskPatch = Partial<
  Pick<Task, 'title' | 'notes' | 'status' | 'priority' | 'dueDate' | 'labels' | 'remindAt'>
>;

export interface MoveTaskInput {
  taskId: string;
  status: TaskStatus;
  /** The task the moved card sits below. Null means the top of the column. */
  beforeId: string | null;
  /** The task the moved card sits above. Null means the bottom of the column. */
  afterId: string | null;
}

export interface MoveTaskResponse {
  task: Task;
  /** The server rewrote the column's ranks; the client should take `column`. */
  rebalanced: boolean;
  column: Task[];
}

export async function listTasks(includeArchived = false) {
  return apiClient.get<{ tasks: Task[]; statuses: TaskStatus[] }>('/personal-organizer/tasks', {
    params: { includeArchived },
  });
}

export async function createTask(data: CreateTaskInput) {
  return apiClient.post<{ task: Task }>('/personal-organizer/tasks', data);
}

export async function updateTask(id: string, patch: TaskPatch) {
  return apiClient.patch<{ task: Task }>(`/personal-organizer/tasks/${id}`, patch);
}

export async function deleteTask(id: string) {
  return apiClient.delete<{ success: boolean }>(`/personal-organizer/tasks/${id}`);
}

export async function moveTask({ taskId, status, beforeId, afterId }: MoveTaskInput) {
  return apiClient.post<MoveTaskResponse>(`/personal-organizer/tasks/${taskId}/move`, {
    status,
    beforeId,
    afterId,
  });
}

export async function reorderColumn(status: TaskStatus, orderedIds: string[]) {
  return apiClient.post<{ column: Task[] }>('/personal-organizer/tasks/reorder', {
    status,
    orderedIds,
  });
}

export async function bulkUpdateTasks(
  ids: string[],
  action: 'complete' | 'archive' | 'delete' | 'reopen',
) {
  return apiClient.post<{ affected: number }>('/personal-organizer/tasks/bulk', { ids, action });
}

export async function getTaskCounts() {
  return apiClient.get<{ counts: TaskCounts }>('/personal-organizer/counts');
}

export async function listSuggestions(state: TaskSuggestion['state'] = 'pending') {
  return apiClient.get<{ suggestions: TaskSuggestion[] }>('/personal-organizer/suggestions', {
    params: { state },
  });
}

export async function acceptSuggestion(id: string, edits: TaskPatch = {}) {
  return apiClient.post<{ task: Task }>(
    `/personal-organizer/suggestions/${id}/accept`,
    edits,
  );
}

export async function rejectSuggestion(id: string, reason?: string) {
  return apiClient.post<{ suggestion: TaskSuggestion }>(
    `/personal-organizer/suggestions/${id}/reject`,
    reason ? { reason } : {},
  );
}

export interface FathomIngest {
  id: string;
  fathomRecordingId: number;
  meetingTitle: string | null;
  shareUrl: string | null;
  recordedAt: string | null;
  transcriptChars: number | null;
  state: 'pending' | 'fetched' | 'extracted' | 'skipped' | 'failed';
  skipReason: 'no_transcript' | 'too_short' | 'internal_only' | 'no_tasks' | 'filtered_out' | null;
  suggestionsCreated: number;
  error: string | null;
  createdAt: string;
}

export async function listFathomIngests(limit = 25) {
  /** `total` = every call ever checked; older API builds omit it. */
  return apiClient.get<{ ingests: FathomIngest[]; total?: number }>('/personal-organizer/fathom/ingests', {
    params: { limit },
  });
}

/** Admin-only: queues a Fathom poll and returns the job id. */
export async function syncFathom(lookbackHours?: number) {
  return apiClient.post<{ jobId: string | null; status: 'queued' }>(
    '/personal-organizer/fathom/sync',
    lookbackHours ? { lookbackHours } : {},
  );
}

/** What one manual check looked at, and why it found nothing if it did. */
export interface FathomSyncResult {
  meetingsScanned: number;
  extracted: number;
  skipped: number;
  failed: number;
  suggestionsCreated: number;
  reason?: string;
  /** Whether `reason` needs fixing. A quiet window does not. */
  reasonActionable?: boolean;
}

export type FathomSyncStatus =
  | { status: 'running' }
  | { status: 'ready'; result: FathomSyncResult }
  | { status: 'error'; error: string }
  | { status: 'not_found' };

/** Poll a queued check. `not_found` means the job was archived, not that it failed. */
export async function getFathomSyncStatus(jobId: string) {
  return apiClient.get<FathomSyncStatus>(`/personal-organizer/fathom/sync/${jobId}`);
}

// ── Calendar ────────────────────────────────────────────────────────────────

export type CalendarItemKind = 'meeting' | 'task' | 'reminder';

export interface MeetingMeta {
  kind: 'meeting';
  location: string | null;
  conferenceUrl: string | null;
  attendeeCount: number;
  responseStatus: 'needsAction' | 'declined' | 'tentative' | 'accepted' | null;
  busy: boolean;
  recurring: boolean;
}

export interface CalendarTaskMeta {
  kind: 'task';
  taskId: string;
  status: TaskStatus;
  priority: TaskPriority;
  overdue: boolean;
  source: TaskSource;
}

export interface CalendarReminderMeta {
  kind: 'reminder';
  taskId: string;
  taskStatus: TaskStatus;
}

export interface CalendarItem {
  /** Kind-prefixed, so a task and its reminder never collide as keys. */
  id: string;
  kind: CalendarItemKind;
  title: string;
  /** Every Brussels day this occupies — precomputed server-side. */
  days: string[];
  startsAt: string | null;
  endsAt: string | null;
  allDay: boolean;
  /** 'HH:MM' in Brussels. The client does no timezone maths. */
  startTimeLabel: string | null;
  href: string | null;
  meta: MeetingMeta | CalendarTaskMeta | CalendarReminderMeta;
}

export interface CalendarPayload {
  from: string;
  to: string;
  /** Brussels today, so "today" agrees with the digest and the reminders. */
  today: string;
  timezone: 'Europe/Brussels';
  items: CalendarItem[];
  google: {
    state: 'ok' | 'not_connected' | 'revoked' | 'unavailable';
    email: string | null;
    message: string | null;
    fetchedAt: string | null;
  };
}

export async function getOrganizerCalendar(from: string, to: string) {
  return apiClient.get<CalendarPayload>('/personal-organizer/calendar', {
    params: { from, to },
  });
}
